/* The public card catalog's filter and order (CardCatalog.tsx), kept free of React
 * so it can be tested on its own. */

export const RARITIES = ['common', 'rare', 'epic', 'legendary', 'relic'] as const;
export const TYPES = ['character', 'influencer', 'token'] as const;
export const FACTIONS = ['alien', 'cyborg', 'dragon', 'human', 'hybrid', 'incrypt', 'mech'] as const;

export type Rarity = (typeof RARITIES)[number];
export type CardType = (typeof TYPES)[number];
export type Faction = (typeof FACTIONS)[number];

/** One card as GET /tcg-public/cards sends it. The art is not in the payload: it
 *  comes from S3 by type/name/rarity (cardArtUrl), the same as the portal's grid. */
export type CatalogCard = {
  id: number | string;
  name: string;
  rarity: string;
  type: string;
  faction: string;
};

export type CardFilters = {
  q?: string | null;
  rarity?: string | null;
  type?: string | null;
  faction?: string | null;
};

/* A value this page does not know (a hand-edited link, a rarity since renamed)
 * reads as "All" rather than "nothing matches": the reader still gets cards, and
 * the chips show what is actually applied. Case is ignored. */
export const asRarity = (v?: string | null): Rarity | '' => {
  const s = String(v ?? '').trim().toLowerCase();
  return (RARITIES as readonly string[]).includes(s) ? (s as Rarity) : '';
};

export const asType = (v?: string | null): CardType | '' => {
  const s = String(v ?? '').trim().toLowerCase();
  return (TYPES as readonly string[]).includes(s) ? (s as CardType) : '';
};

export const asFaction = (v?: string | null): Faction | '' => {
  const s = String(v ?? '').trim().toLowerCase();
  return (FACTIONS as readonly string[]).includes(s) ? (s as Faction) : '';
};

const rank = (r: string) => {
  const i = (RARITIES as readonly string[]).indexOf(r.toLowerCase());
  return i === -1 ? RARITIES.length : i;
};

const byName = new Intl.Collator('en', { sensitivity: 'base' });

/* Name, then rarity in rarity order (common → relic) — the order the API sends,
 * restated here so the grid never depends on it. The same name exists at several
 * rarities ("ethereum" rare and "ethereum" legendary are two cards), so the
 * rarity step is what keeps a name's cards together and in a sensible order; the
 * id settles anything left so the order is the same on every render. */
export const compareCards = (a: CatalogCard, b: CatalogCard): number =>
  byName.compare(a.name, b.name) ||
  rank(a.rarity) - rank(b.rarity) ||
  String(a.id).localeCompare(String(b.id), 'en', { numeric: true });

/* Name: case-insensitive, anywhere in it, surrounding spaces ignored. Rarity,
 * type and faction: one each, exact. Returns a new, sorted array; the input is
 * left alone. */
export function filterCards(
  cards: CatalogCard[],
  { q, rarity, type, faction }: CardFilters = {}
): CatalogCard[] {
  const needle = String(q ?? '').trim().toLowerCase();
  const r = asRarity(rarity);
  const t = asType(type);
  const f = asFaction(faction);
  return cards
    .filter(
      (c) =>
        (!r || c.rarity.toLowerCase() === r) &&
        (!t || c.type.toLowerCase() === t) &&
        (!f || c.faction.toLowerCase() === f) &&
        (!needle || c.name.toLowerCase().includes(needle))
    )
    .sort(compareCards);
}
